import { Button, ButtonProps, Link, LinkProps } from '@sparemin/blockhead';
import { noop, over } from 'lodash-es';
import React, { useCallback } from 'react';
import { useAuth } from 'ui/context';
import { NavigatableAuthStep } from 'ui/types';

export type AuthNavLinkAppearance = 'link' | 'button';

export interface BaseAuthNavLinkProps {
  /**
   * The target step to transition to
   */
  to: NavigatableAuthStep;
}

export type AuthNavLinkAppearanceProps = BaseAuthNavLinkProps &
  LinkProps & {
    appearance: Extract<AuthNavLinkAppearance, 'link'>;
  };

export type AuthNavButtonAppearanceProps = BaseAuthNavLinkProps &
  ButtonProps & {
    appearance: Extract<AuthNavLinkAppearance, 'button'>;
  };

export type AuthNavLinkProps =
  | AuthNavLinkAppearanceProps
  | AuthNavButtonAppearanceProps;

function getComponentProps(props: AuthNavButtonAppearanceProps): ButtonProps;
function getComponentProps(props: AuthNavLinkAppearanceProps): LinkProps;
function getComponentProps(props: AuthNavLinkProps): unknown {
  const { appearance, onPress, to, ...rest } = props;
  return rest;
}

/**
 * A link to an Authentication step.
 *
 * The Authentication UI can be navigated with links where each step is on a separate page
 * or with callbacks for when the UI is used inside of modal.  This component will
 * render the correct "link" to another auth step - an anchor tag or a button -
 * depending on the navigation context.
 */
const AuthNavLink: React.FC<AuthNavLinkProps> = (props) => {
  const { onPress = noop, to } = props;
  const authContext = useAuth();
  const { navigation, navigationType } = authContext;

  const transition =
    navigationType === 'modal' ? navigation.onStepChange : undefined;

  const handlePress = useCallback(() => {
    transition?.(to);
  }, [to, transition]);

  // this condition amounts to "does not have route" and indicates whether the underlying
  // component should be a button, but the conditions are left inline rather
  // than in a utility function with a descriptive name because inlining them prevents
  // TS errors when we fall through the if.  In that case, TS can infer some things
  // about the data (e.g. that navigationType is 'url' and navigation.linkComponent
  // exists).
  if (
    // need to be able to index `navigation.routes` with `to` in order to check for
    // existence of a route.  since forgot-password is only implemented as a modal,
    // it cannot be used to index routes and has to be filtered out of `to`'s possible
    // values
    to === 'forgot-password' ||
    // need to be able to index `navigation.routes` to check for a route.  In order to
    // be certain that `navigation.routes` exists, we need to know that `navigationType`
    // is 'url'
    navigationType === 'modal' ||
    // if the code reaches here, we know that `to` is a NavigatableAuthStep and that
    // `navigation` is AuthURLNavigationConfig, so we can finally check if a route for
    // the destination AuthStep exists
    !navigation.routes[to]
  ) {
    const commonButtonProps = {
      onPress: over([handlePress, onPress]),
      type: 'button',
    } as const;

    return props.appearance === 'link' ? (
      <Link
        as="button"
        elementType="button"
        {...commonButtonProps}
        {...getComponentProps(props)}
      />
    ) : (
      <Button {...commonButtonProps} {...getComponentProps(props)} />
    );
  }

  const { linkComponent } = navigation;

  const commonLinkProps = {
    href: navigation.routes[to],
    as: linkComponent,
    onPress: onPress,
  } as const;

  return props.appearance === 'link' ? (
    <Link {...commonLinkProps} {...getComponentProps(props)} />
  ) : (
    <Button
      {...commonLinkProps}
      {...getComponentProps(props)}
      onPress={over([handlePress, onPress])}
    />
  );
};

export default AuthNavLink;
